import { Modal, Tab, Tabs } from 'react-bootstrap';
import { PriceList } from '../../../models/Shop';
import { ChangeEvent, useEffect, useState } from 'react';
import { addPriceListAPI } from '../../../services/shopsApi';
import { toastWarning } from '../../../utils/toastMethod';
import { setPriceListUploaded } from '../../../store/priceListSlice';
import { useDispatch } from 'react-redux';
import PriceListTable from '../PriceListTable';
import Input from '../../Input';
import TextArea from '../../TextArea';
import infoIcon from '../../../assets/images/info-icon.svg';
import Switch from '../../Switch';
import useCheckMobileResolution from '../../../hook/useCheckMobileResolution';
import styles from './style.module.css';

interface PriceListPreviewProps {
  show: boolean;
  setShow: (show: boolean) => void;
  data: Record<string, any[]>;
  companyId: string | undefined;
  shopId: string | undefined;
}

export default function PriceListPreview({ show, setShow, data, companyId, shopId }: PriceListPreviewProps) {
  const dispatch = useDispatch();
  const [priceList, setPriceList] = useState<PriceList[] | null>(null);
  const [confirmButtonDisabled, setConfirmButtonDisabled] = useState<boolean>(false);
  const isMobile = useCheckMobileResolution();

  const handleNameChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    if (priceList && event.target.value.trim() !== '') {
      const updated = priceList.map((pl, i) => (i === index ? { ...pl, name: event.target.value } : pl));
      setPriceList(updated);
    }
  };

  const handleDescriptionChange = (index: number, event: ChangeEvent<HTMLTextAreaElement>) => {
    if (priceList) {
      const updated = priceList.map((pl, i) => (i === index ? { ...pl, description: event.target.value } : pl));
      setPriceList(updated);
    }
  };

  const handleActiveChange = (checked: boolean, index?: number) => {
    if (priceList) {
      const updated = priceList.map((pl, i) => (i === index ? { ...pl, isActive: checked } : pl));
      setPriceList(updated);
    }
  };

  const handleDefaultChange = (checked: boolean, index?: number) => {
    if (priceList) {
      const updated = priceList.map((pl, i) => (i === index ? { ...pl, isDefault: checked } : pl));
      setPriceList(updated);
    }
  };

  const handleConfirm = () => {
    if (!priceList) return;

    if (!priceList.find((pl) => pl.isDefault))
      return toastWarning('Attenzione! Devi impostare un listino prezzi come predefinito.');

    if (priceList.filter((pl) => pl.isDefault).length > 1)
      return toastWarning('Attenzione! Puoi avere un solo listino prezzi predefinito.');

    if (priceList.some((pl) => pl.isDefault && !pl.isActive))
      return toastWarning('Attenzione! Il listino prezzi predefinito deve essere attivo.');

    if (priceList.some((pl) => pl.name.trim() === ''))
      return toastWarning('Attenzione! Devi inserire un nome valido per tutti i listino prezzi.');

    setConfirmButtonDisabled(true);
    addPriceListAPI(companyId, shopId, priceList)
      .then(() => {
        setShow(false);
        dispatch(setPriceListUploaded(true));
      })
      .finally(() => {
        setConfirmButtonDisabled(false);
      });
  };

  useEffect(() => {
    if (show && data) {
      const priceLists: PriceList[] = Object.entries(data).map(([sheetName, rows], index) => {
        if (!Array.isArray(rows) || rows.length < 2)
          return {
            name: sheetName,
            description: '',
            language: '',
            isDefault: false,
            isActive: false,
            items: [],
          };
        // rows[0] is the Excel file header
        const items = rows
          .slice(1)
          .filter((row: any[]) => row.some((cell) => cell !== undefined && cell !== null && String(cell).trim() !== ''))
          .map((row: any[]) => {
            let priceValue = row[5] ? String(row[5]) : '';
            if (priceValue !== '' && !isNaN(Number(priceValue))) {
              priceValue = `${priceValue} €`;
            }
            return {
              category: row[0] ? String(row[0]) : '',
              subCategory: row[1] ? String(row[1]) : '',
              name: row[2] ? String(row[2]) : '',
              description: row[3] ? String(row[3]) : '',
              notes: row[4] ? String(row[4]) : '',
              price: priceValue,
              imageUrl: '',
            };
          });
        return {
          name: sheetName,
          description: '',
          language: '',
          isDefault: index === 0,
          isActive: true,
          items,
        };
      });

      setPriceList(priceLists);
    }
  }, [data, show]);

  return (
    <div className={styles.priceListPreviewModal}>
      {priceList && priceList.length > 0 ? (
        <Modal
          show={show}
          onHide={() => setShow(false)}
          centered
          dialogClassName={styles.priceListPreviewModal}
          contentClassName={styles.priceListPreviewModalBody}
        >
          <Modal.Header closeButton>
            <Modal.Title>Verifica e conferma il tuo listino prezzi</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Tabs className="mb-3">
              {priceList.map((list, index) => (
                <Tab eventKey={index} title={list.name} key={index}>
                  <div
                    style={
                      isMobile
                        ? { display: 'flex', flexDirection: 'column' }
                        : { display: 'flex', alignItems: 'center', gap: '20px' }
                    }
                  >
                    <Input
                      styleContainer={{ fontSize: '14px', maxWidth: '300px' }}
                      label="Nome listino"
                      text={list.name}
                      placeholder="Nome listino"
                      handleChange={(e) => handleNameChange(index, e)}
                    />
                    <Switch
                      styleContainer={
                        isMobile ? { width: '250px', marginTop: '5px' } : { width: '250px', marginTop: '30px' }
                      }
                      index={index}
                      isActive={list.isDefault}
                      activeLabel="Predefinito"
                      notActiveLabel="Non Predefinito"
                      handleActiveChange={handleDefaultChange}
                      infoImageSrc={infoIcon}
                      infoImageAlt="infoIcon"
                      infoImageStyle={{ width: '20px', marginLeft: '10px', opacity: '0.8' }}
                      infoImageTooltipId="info-icon-is-default"
                      infoImageTooltipText={`Selezionando questa opzione, il listino prezzi "${list.name}" sarà quello predefinito per il tuo negozio. Ovvero il primo ad essere mostrato.`}
                    />
                    <Switch
                      styleContainer={
                        isMobile ? { width: '250px', marginTop: '10px' } : { width: '250px', marginTop: '30px' }
                      }
                      index={index}
                      isActive={list.isActive}
                      activeLabel="Attivo"
                      notActiveLabel="Non Attivo"
                      handleActiveChange={handleActiveChange}
                      infoImageSrc={infoIcon}
                      infoImageAlt="infoIcon"
                      infoImageStyle={{ width: '20px', marginLeft: '10px', opacity: '0.8' }}
                      infoImageTooltipId="info-icon-is-active"
                      infoImageTooltipText={`Attiva o Disattiva la visualizzazione del listino prezzi "${list.name}". Se disattivo, il listino non sarà visibile ai tuoi clienti.`}
                    />
                  </div>
                  <TextArea
                    styleContainer={{ marginTop: '10px', fontSize: '14px' }}
                    title="Descrizione"
                    text={list.description}
                    placeholder="Inserisci la descrizione della tuo negozio"
                    handleChange={(e) => handleDescriptionChange(index, e)}
                  />
                  <PriceListTable priceList={list} />
                </Tab>
              ))}
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={confirmButtonDisabled}>
              Conferma
            </button>
          </Modal.Footer>
        </Modal>
      ) : null}
    </div>
  );
}
